import { AppTimestampContext } from "global/cnr/contexts/AppTimestampContext";
import { TimestampsContext } from "global/cnr/contexts/TimestampsContext";
import { shouldUseCache } from "global/cnr/contexts/useHelpers/useHelpers";
import { createRefPath_event } from "global/firestoreData/appData/appRefPaths";
import _ from 'lodash';
import { useCallback, useContext, useState } from "react";
import { _showAppLogs } from "../../../global/cnr/contexts/LogContext";
import { useCacheDataManager } from "../../../global/cnr/contexts/useHelpers/useCacheDataManager";
import { useMultiDoc } from "../../../global/cnr/contexts/useHelpers/useMultiDoc";
import { _cacheGroupTypes } from "../../../global/cnr/contexts/useHelpers/useTimestamp";
import { seasonalHelpers } from "../cnr/reducers/reducerHelpers/seasonalHelpers";
import { _sportsInfoTypes } from "./useSportsInfo";
import { ParentContext } from "global/cnr/contexts/ParentContext";
import { _matchSeasonalCollection, _resultsSeasonalCollection } from "../cnr/reducers/SportsSeasonReducer";

export const _allowResultsUpdate = true

const _applyCache = true

const dataItems_sportsSeasonMatches = {
  matches_currentSeason: {
    combineSubs: true,
    renderAs: 'matches',
    collectionName: _matchSeasonalCollection,
    ignoreCache: true,
    sportPropName: 'seasonMatches',
    collectionSubs: ['matches', 'playoffMatches'],
    subItemKey: '_matchCollectionKey'
  },
}

export const useSportMatches = ({ sportsInfoType, pathViews }) => {

  const parentContext = useContext(ParentContext);
  const { fns } = parentContext ?? {}
  const { database_fns } = fns

  const cache_groupType = _cacheGroupTypes.sportsSeasonMatchesInfo;

  // appTimestampContext 
  const appTimestampContext = useContext(AppTimestampContext);
  const { appTimestamp_state } = appTimestampContext ?? {}
  const { timestampInfo } = appTimestamp_state ?? {}
  const { timestamps } = timestampInfo ?? {}

  // timestampsContext
  const timestampsContext = useContext(TimestampsContext)
  const { timestamps_state } = timestampsContext ?? {}
  const { _validatedTimestamps } = timestamps_state ?? {}

  const [updating, setUpdating] = useState()

  const { handleGet_multiDocs_noListen } = useMultiDoc()
  const cdm = useCacheDataManager()

  const handle_sportsMatchesInfo = async ({ sports_key, sportsMatchesData, pdks, callback, fromCache, newTimestamp }) => {
    const sportsSeason_datas = seasonalHelpers.ammend_sportsSeasonDataz(sportsMatchesData, '2026')
    sportsSeason_datas.parentDataKeys = pdks
    sportsSeason_datas._validatedTimestamp = newTimestamp
    _showAppLogs.sportInfo && console.log(`✅${fromCache ? '🗄️' : '🔥'}${sportsInfoType}`, sportsSeason_datas)
    if (!fromCache) {
      const fullData = { sportsMatchesData }
      await cdm.mergeToGroup(cache_groupType, sports_key, fullData, newTimestamp, true);
    }
    callback(sportsSeason_datas)
  }

  /**
   * handles the returned data from _refPath_s
   * @param {object} sport 
   */
  const handleDbData_sportsSeasonMatches = (sports_key, sport, cb_main, ts) => {
    const cb = async (sportsMatchesData) => handle_sportsMatchesInfo({ sports_key, sportsMatchesData, callback: cb_main, fromCache: false, newTimestamp: ts })
    const { latestSeason } = sport ?? {}
    const _pks = seasonalHelpers.getPks(pathViews, latestSeason, sports_key, ['teams'])
    handleGet_multiDocs_noListen({
      key: 'sportsResults',
      sportsDocs: dataItems_sportsSeasonMatches,
      pathViews,
      itemKey: sports_key,
      pks: _pks,
      callback: cb
    })
  }

  const getSportsMatches = useCallback(async (_sportsInfoType, sports_key, callback, db_ts_seasonMatches) => {

    const cgt = _sportsInfoType + 'Info';

    const cb_database = (data) => {
      switch (_sportsInfoType) {
        case _sportsInfoTypes.sportsSeasonMatches:
          console.log('sports_key, data', sports_key, data)
          handleDbData_sportsSeasonMatches(sports_key, data, callback, db_ts_seasonMatches)
      }
    }

    const cb_cache = (cachedData) => {
      // fromCache and newTimestamp included in cachedData
      switch (_sportsInfoType) {
        case _sportsInfoTypes.sportsSeasonMatches:
          handle_sportsMatchesInfo({ ...cachedData, sports_key, callback, fromCache: true })
      }
    }

    switch (_sportsInfoType) {
      case _sportsInfoTypes.sportsSeasonMatches:
        getResultsItemData(cdm, db_ts_seasonMatches, cgt, pathViews, sports_key, 'sports', true, database_fns, cb_cache, cb_database, _validatedTimestamps)
    }
  }, [])

  const getSportsMatches_year = useCallback(async ({ matchesSeasonalKey, callback }) => {

    const cb = ({ matches, playoffMatches, results, playoffResults }) => {
      const combinedMatches = _.assign({}, ..._.map(matches, 'matches'))
      callback({ matches, playoffMatches, results, playoffResults })
    }

    const _refPath_matches = createRefPath_event(pathViews, [
      `_${_matchSeasonalCollection}`,
      matchesSeasonalKey,
      'matches'
    ])

    const _refPath_playoffs = createRefPath_event(pathViews, [
      `_${_matchSeasonalCollection}`,
      matchesSeasonalKey,
      'playoffMatches'
    ])

    const _refPath_results = createRefPath_event(pathViews, [
      `_${_resultsSeasonalCollection}`,
      matchesSeasonalKey,
      'results'
    ])

    const _refPath_playoffResults = createRefPath_event(pathViews, [
      `_${_resultsSeasonalCollection}`,
      matchesSeasonalKey,
      'playoffResults'
    ])

    const [matches, playoffMatches, results, playoffResults] = await Promise.all([
      database_fns.get_data({ refPath: _refPath_matches }),
      database_fns.get_data({ refPath: _refPath_playoffs }),
      database_fns.get_data({ refPath: _refPath_results }),
      database_fns.get_data({ refPath: _refPath_playoffResults }),
    ])

    cb({
      matches,
      playoffMatches,
      results,
      playoffResults
    })
  },
    []
  )


  return { getSportsMatches, getSportsMatches_year, updating }

};

const getResultsItemData = async (
  cdm,
  currentTimestamp_db,
  cgt,
  pathViews,
  sportsKey,
  collectionName,
  ignoreId,
  database_fns,
  callback_cache,
  callback_database,
  _validatedTimestamps
) => {

  // get the cache
  const cache_result = await cdm.getSubItem(cgt, sportsKey);

  const { _timestamp: timestamp_cache } = cache_result ?? {}

  // KEY GUARD: already have this version 
  const validatedTimestamp = _validatedTimestamps?.[cgt] ?? {};

  if (validatedTimestamp && validatedTimestamp === currentTimestamp_db) {
    _showAppLogs.sportInfo && console.log(`✅${cgt}`)
    // return; // 🚫 do nothing
  }

  const allowCache = shouldUseCache(timestamp_cache, currentTimestamp_db, cgt)

  // return the cache data if available
  if (_applyCache && !_.isEmpty(cache_result) && allowCache) {
    callback_cache({ ...cache_result, fromCache: true, newTimestamp: currentTimestamp_db })
    return;
  }

  // get the data from the database
  const _refPath = createRefPath_event(pathViews, [collectionName, sportsKey])
  database_fns.get_doc({
    refPath: _refPath,
    opts: { ignoreId: ignoreId },
    callback: callback_database
  })
}